import { Autocomplete } from '@mui/material'
import React, { useMemo, useRef, useState } from 'react'
import {
  actionButtonsIn,
  AutocompleteActionsContext,
  AutocompleteActionsFocus,
  closeByBlurringInput,
  nextTabbableAfter,
} from './actionsFocus'
import { useAutoCompleteConfig } from './useAutoccompleteConfig'

export const BaseAutocomplete = <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = 'div',
>(
  props: React.ComponentProps<typeof Autocomplete<T, Multiple, DisableClearable, FreeSolo, ChipComponent>>
) => {
  const { makeDefaultNoOptionsText: makeNoOptionsText } = useAutoCompleteConfig()
  // Which empty state to show depends on what has been typed, and an uncontrolled Autocomplete keeps
  // that to itself — so track it here rather than only answering for callers that control it.
  const [typedValue, setTypedValue] = useState('')

  const rootRef = useRef<HTMLDivElement | null>(null)
  // Stays null unless the paper slot pins actions below the options; the handlers then no-op.
  const regionRef = useRef<HTMLElement | null>(null)

  const actionsFocus = useMemo<AutocompleteActionsFocus>(
    () => ({
      registerRegion: (region) => {
        regionRef.current = region
      },
      onRegionKeyDown: (event) => {
        if (event.defaultPrevented) return

        const root = rootRef.current
        if (event.key === 'Escape') {
          // The popup is a sibling of the combobox root, so a key press on an action never reaches
          // MUI's own Escape handling, and left to bubble it would close the dialog the field sits in.
          // Replaying it on the input hands it back to MUI: it closes the popup without blurring, so
          // `clearOnBlur` does not wipe what was typed, and stops the event itself.
          event.preventDefault()
          event.stopPropagation()
          const input = root?.querySelector('input')
          input?.focus({ preventScroll: true })
          input?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))
          return
        }

        const actions = actionButtonsIn(regionRef.current)
        const position = actions.indexOf(document.activeElement as HTMLButtonElement)
        if (event.key !== 'Tab' || position === -1) return

        event.preventDefault()
        const destination = actions[event.shiftKey ? position - 1 : position + 1]
        if (destination !== undefined) {
          destination.focus()
        } else if (event.shiftKey) {
          root?.querySelector('input')?.focus()
        } else {
          closeByBlurringInput(root, root === null ? null : nextTabbableAfter(root))
        }
      },
      onRegionBlur: (event) => {
        const focusMovingTo = event.relatedTarget instanceof HTMLElement ? event.relatedTarget : null
        const stayingInside =
          focusMovingTo !== null &&
          (regionRef.current?.contains(focusMovingTo) === true || rootRef.current?.contains(focusMovingTo) === true)
        if (!stayingInside) closeByBlurringInput(rootRef.current, focusMovingTo)
      },
    }),
    []
  )

  return (
    <AutocompleteActionsContext.Provider value={actionsFocus}>
      <Autocomplete<T, Multiple, DisableClearable, FreeSolo, ChipComponent>
        data-testid="base-autocomplete"
        noOptionsText={makeNoOptionsText(props.inputValue ?? typedValue)}
        {...props}
        ref={rootRef}
        onInputChange={(event, value, reason) => {
          setTypedValue(value)
          props.onInputChange?.(event, value, reason)
        }}
        onKeyDown={(event) => {
          props.onKeyDown?.(event)
          const [firstAction] = actionButtonsIn(regionRef.current)
          // Tabbing backwards out of the input is an ordinary blur, which closes the popup on its own.
          if (event.key !== 'Tab' || event.shiftKey || event.defaultPrevented || firstAction === undefined) return
          event.preventDefault()
          firstAction.focus()
        }}
        onBlurCapture={(event) => {
          props.onBlurCapture?.(event)
          // Left alone, the input's blur handler closes the popup and unmounts the action mid-Tab.
          const region = regionRef.current
          if (event.relatedTarget instanceof Node && region?.contains(event.relatedTarget) === true) {
            event.stopPropagation()
          }
        }}
      />
    </AutocompleteActionsContext.Provider>
  )
}
